import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';
import {
  getMarketById,
  onMarketTenantsForDisplaySnapshot,
  type Market,
  type TenantWithMarketInfo,
} from '@paulownia/shared';
import { useCurrentMarket } from '@/contexts/CurrentMarketContext';
import { MarketHeader } from './MarketHeader';
import { MerchantGrid } from './MerchantGrid';
import { LoadingSpinner } from '../customer/components/LoadingSpinner';

/**
 * Market discovery page showing all merchants at a given market
 */
export function MarketPage() {
  const { marketId } = useParams<{ marketId: string }>();
  const { setMarket: setGlobalMarket } = useCurrentMarket();
  const [market, setMarket] = useState<Market | null>(null);
  const [tenants, setTenants] = useState<TenantWithMarketInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!marketId) {
      setError('Marche non trouve');
      setLoading(false);
      return;
    }

    // Fetch market info — controls the loading state.
    // setLoading(false) is intentionally called here (not in the snapshot
    // callback) to avoid a race condition: the snapshot fires from Firestore
    // cache almost instantly, before getMarketById resolves, which would flip
    // loading=false while market is still null and show a false "not found" page.
    getMarketById(marketId)
      .then((m) => {
        if (!m) {
          setError('Marche non trouve');
        } else {
          setMarket(m);
          // Set this market as the global current market so navigating
          // to a merchant from this page uses the correct context.
          setGlobalMarket({
            marketId: m.id,
            city: m.city,
            dayOfWeek: m.dayOfWeek,
          });
        }
        setLoading(false);
      })
      .catch(() => {
        setError('Erreur de chargement');
        setLoading(false);
      });

    // Real-time subscription for tenants — does not control loading state.
    const unsubscribe = onMarketTenantsForDisplaySnapshot(marketId, (data) => {
      setTenants(data);
    });

    return unsubscribe;
  }, [marketId, setGlobalMarket]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !market) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">{error || 'Marche non trouve'}</p>
          <Link to="/" className="text-orange-600 hover:underline">
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{market.city} - Commercants | Paulownia</title>
        <meta
          name="description"
          content={`Decouvrez les ${tenants.length} commercants du ${market.city}. Commandez en ligne et recuperez vos achats sans faire la queue.`}
        />
        <meta property="og:title" content={`${market.city} - Commercants | Paulownia`} />
        <meta
          property="og:description"
          content={`Decouvrez les ${tenants.length} commercants du ${market.city}. Commandez en ligne.`}
        />
        <link rel="canonical" href={`https://order-paulownia.web.app/market/${marketId}`} />
      </Helmet>

      <div className="h-full overflow-y-auto bg-gray-50">
        <div className="container mx-auto px-4 py-6 max-w-4xl">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Retour à l'accueil
          </Link>

          <MarketHeader market={market} merchantCount={tenants.length} />
          <MerchantGrid tenants={tenants} marketId={marketId} />
        </div>
      </div>
    </>
  );
}
